import type { CSSProperties, ElementType, ReactNode } from 'react';
import { useInView } from 'react-intersection-observer';
import { cn } from '@rocco/utils/cn';

type ScrollRevealAnimation =
    | 'fade-up'
    | 'fade-in'
    | 'fade-left'
    | 'fade-right'
    | 'scale-settle';

interface ScrollRevealProps {
    children: ReactNode;
    animation?: ScrollRevealAnimation;
    duration?: number;
    delay?: number;
    threshold?: number;
    className?: string;
    as?: ElementType;
    style?: CSSProperties;
}

const INITIAL_ANIMATION_CLASS: Record<ScrollRevealAnimation, string> = {
    'fade-up': 'translate-y-8 opacity-0',
    'fade-in': 'opacity-0',
    'fade-left': '-translate-x-6 opacity-0',
    'fade-right': 'translate-x-6 opacity-0',
    'scale-settle': 'scale-[1.06] opacity-0',
};

const VISIBLE_ANIMATION_CLASS: Record<ScrollRevealAnimation, string> = {
    'fade-up': 'translate-y-0 opacity-100',
    'fade-in': 'opacity-100',
    'fade-left': 'translate-x-0 opacity-100',
    'fade-right': 'translate-x-0 opacity-100',
    'scale-settle': 'scale-100 opacity-100',
};

/** Scroll-triggered reveal wrapper used by the HDE page sections. */
export const ScrollReveal = ({
    children,
    animation = 'fade-up',
    duration = 700,
    delay = 0,
    threshold = 0.15,
    className,
    as: Component = 'div',
    style,
}: ScrollRevealProps) => {
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold,
    });

    const transitionStyle: CSSProperties = {
        transitionDuration: `${duration}ms`,
        transitionDelay: `${delay}ms`,
        ...style,
    };

    return (
        <Component
            ref={ref}
            className={cn(
                'will-change-opacity transform-gpu transition-all ease-out will-change-transform',
                inView
                    ? VISIBLE_ANIMATION_CLASS[animation]
                    : INITIAL_ANIMATION_CLASS[animation],
                className,
            )}
            style={transitionStyle}
        >
            {children}
        </Component>
    );
};
